/**
 * Autocomplete for the mail search box: what the word under the caret can become. Pure functions;
 * the search bar fetches people and renders the list. Operator names come from the query parser's
 * locale aliases (`von:` in German), so a suggestion is always something the parser accepts.
 */
import { matchOperators, operatorKeyword, operatorOf, SEARCH_OPERATORS, type SearchOperator } from '@platform/search-query';

export type SearchLocale = 'en' | 'de';

/** Operators whose value is a person; the search bar suggests people from the address book. */
export const PERSON_OPERATORS: readonly SearchOperator[] = ['from', 'to', 'cc', 'participants'];

/** Operators shown when the box is focused and empty. */
export const COMMON_OPERATORS: readonly SearchOperator[] = ['from', 'to', 'subject', 'body', 'hasattachment', 'received'];

export interface Word {
  /** Start of the part that gets replaced (after a leading `-` or `(`). */
  start: number;
  end: number;
  text: string;
}

/** The word around `caret`, or undefined inside a quoted phrase. */
export function wordAt(value: string, caret: number): Word | undefined {
  let start = caret;
  while (start > 0 && !/\s/.test(value[start - 1] ?? '')) start--;
  let end = caret;
  while (end < value.length && !/\s/.test(value[end] ?? '')) end++;
  const quotesBefore = (value.slice(0, start).match(/"/g) ?? []).length;
  if (quotesBefore % 2 === 1) return undefined;
  while (start < end && (value[start] === '-' || value[start] === '(')) start++;
  return { start, end, text: value.slice(start, end) };
}

export type Intent =
  | { kind: 'operator'; prefix: string }
  | { kind: 'value'; operator: SearchOperator; partial: string };

/** What the word under the caret is being typed as, or undefined when nothing should be suggested. */
export function intentAt(value: string, caret: number, locale: SearchLocale): { word: Word; intent: Intent } | undefined {
  const word = wordAt(value, caret);
  if (!word) return undefined;
  const colon = word.text.indexOf(':');
  if (colon < 0) return word.text.length >= 2 && !word.text.includes('"') ? { word, intent: { kind: 'operator', prefix: word.text } } : undefined;
  const operator = operatorOf(word.text.slice(0, colon), locale);
  if (!operator) return undefined;
  return { word, intent: { kind: 'value', operator, partial: word.text.slice(colon + 1).replace(/^"/, '') } };
}

/** True while the typed text may still become an operator (`fro`, `from:al`, quotes, groups): the list stays unfiltered until Enter. */
export function looksLikeOperator(query: string, locale: SearchLocale): boolean {
  return /[:"()]/.test(query) || query.split(/\s+/).some((word) => word.length >= 2 && matchOperators(word, locale).length > 0);
}

export interface OperatorSuggestion {
  operator: SearchOperator;
  keyword: string;
}

export function operatorSuggestions(prefix: string, locale: SearchLocale): OperatorSuggestion[] {
  return matchOperators(prefix, locale);
}

/** The operators of the empty-box tips. */
export function commonOperators(locale: SearchLocale): OperatorSuggestion[] {
  return COMMON_OPERATORS.map((operator) => ({ operator, keyword: operatorKeyword(operator, locale) }));
}

/** Fixed value choices of an operator (the parser accepts these words). */
export function valueChoices(operator: SearchOperator, locale: SearchLocale): string[] {
  const de = locale === 'de';
  if (operator === 'hasattachment' || operator === 'isread' || operator === 'isflagged') return de ? ['ja', 'nein'] : ['yes', 'no'];
  if (operator === 'importance') return de ? ['niedrig', 'normal', 'hoch'] : ['low', 'normal', 'high'];
  if (operator === 'received' || operator === 'sent') return ['today', 'yesterday', '"last week"'];
  return [];
}

/** Replaces `word` with `replacement` (a trailing space is added when `done`), returning the value and caret. */
export function applyToWord(value: string, word: Word, replacement: string, done: boolean): { value: string; caret: number } {
  const tail = value.slice(word.end);
  const insert = done && !tail.startsWith(' ') ? `${replacement} ` : replacement;
  const next = `${value.slice(0, word.start)}${insert}${tail}`;
  const caret = word.start + insert.length + (done && tail.startsWith(' ') ? 1 : 0);
  return { value: next, caret };
}

/** `keyword:` for an operator at the value position, e.g. completing `fro` to `from:`. */
export const operatorText = (suggestion: OperatorSuggestion): string => `${suggestion.keyword}:`;

export { SEARCH_OPERATORS };
